import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { captureException } from '@/lib/sentry';

interface Props {
  children: ReactNode;
  flowName: string;
  message?: string;
}

interface State {
  hasError: boolean;
}

/**
 * Targeted error boundary for specific flows (checkout, request form, etc.).
 * Catches errors without crashing the entire app and shows a contextual recovery message.
 */
export class FlowErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): Partial<State> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error(`[${this.props.flowName}] Error:`, error);
    captureException(error, {
      extra: { componentStack: errorInfo.componentStack },
      tags: { boundary: this.props.flowName },
    });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center p-8 text-center gap-4">
          <AlertTriangle className="w-10 h-10 text-destructive" />
          <p className="text-foreground font-medium">
            {this.props.message || 'Something went wrong. Please try again.'}
          </p>
          <Button
            variant="outline"
            onClick={() => this.setState({ hasError: false })}
          >
            Try Again
          </Button>
        </div>
      );
    }

    return this.props.children;
  }
}
